/**
 * Pure, framework-agnostic per-entity access helpers.
 *
 * These work directly off a `Permissions` object and an entity descriptor — no
 * React, no store. They are the lower half of `useEntityAccess` and are also
 * usable from non-React contexts (route loaders, mutation handlers, tests).
 */
import type { Permissions } from '@/rbac/grants'
import { PERMISSIONS } from '@/rbac/permissions'
import type { AllOrSpecific, FilteredList, StreamAccessFilter } from '@/rbac/types'
import { ANOMALY_STREAM, DEVICE_STATE_STREAM } from '@/utils/streamColumns'

/** Action-type modes used by entity descriptors for filtered-list grants. */
export type ActionTypeAccessMode = 'can_modify' | 'can_view' | 'can_delete'

/**
 * Operation a stream entity check is gating. Determines which stream-field
 * permission key (`STREAM_FIELD_UPDATE`/`DELETE`) is consulted for
 * field-level write checks. Defaults to `'view'`.
 */
export type StreamAccessAction = 'view' | 'update' | 'delete'

/**
 * Operation a device-state-field check is gating — selects the matching
 * permission key (`DEVICE_STATE_FIELD_READ`/`UPDATE`/`DELETE`). Defaults to `'view'`.
 */
export type DeviceStateFieldAccessAction = 'view' | 'update' | 'delete'

/**
 * Operation an exporter entity check is gating — selects the matching
 * filtered-list permission (`EXPORTER_READ` / `UPDATE` / `DELETE`).
 * Defaults to `'view'`.
 */
export type ExporterAccessAction = 'view' | 'update' | 'delete'

/**
 * Operation a subscriber group entity check is gating — selects the matching
 * filtered-list permission (`SUBSCRIBER_GROUP_READ` / `UPDATE` / `DELETE`).
 * Defaults to `'view'`.
 */
export type SubscriberGroupAccessAction = 'view' | 'update' | 'delete'

/**
 * Operation a session rule entity check is gating — selects the matching
 * filtered-list permission (`SESSION_RULE_READ` / `UPDATE` / `DELETE`).
 * Defaults to `'view'`.
 */
export type SessionRuleAccessAction = 'view' | 'update' | 'delete'

/**
 * Operation a DBC parser entity check is gating — selects the matching
 * filtered-list permission (`DBC_PARSER_READ` / `UPDATE` / `DELETE` / `FILE_DOWNLOAD`).
 * Defaults to `'view'`.
 */
export type DbcParserAccessAction = 'view' | 'edit' | 'delete' | 'download' | 'reconfigure'

/**
 * Operation a firmware artifact check is gating — selects the matching
 * filtered-list permission. Defaults to `'view'` (`FIRMWARE_READ`).
 */
export type FirmwareAccessAction = 'view' | 'download' | 'edit' | 'delete'

/** Operation a file asset check is gating — selects the matching filtered-list permission. Defaults to `'view'` (`FILES_READ`). */
export type FileAccessAction = 'view' | 'download' | 'edit' | 'delete'

/**
 * Operation a configuration asset check is gating — selects the matching filtered-list
 * permission from the common `config:*` family shared by JSON and geofence configs.
 * Defaults to `'view'` (`CONFIG_READ`).
 */
export type ConfigAccessAction = 'view' | 'edit' | 'delete'

/**
 * What we're checking against — describes a single entity by resource + identifying fields.
 */
export type EntityDescriptor =
	| {
			kind: 'device'
			fields: Record<string, string>
	  }
	| {
			kind: 'stream'
			name: string
			field?: string
			action?: StreamAccessAction
	  }
	| {
			kind: 'device_state_field'
			field?: string
			action?: DeviceStateFieldAccessAction
	  }
	| {
			kind: 'action_type'
			name: string
			mode: ActionTypeAccessMode
	  }
	| {
			kind: 'action_type_id'
			id: string
			mode: ActionTypeAccessMode
	  }
	| {
			kind: 'metadata'
			fields: Record<string, string>
	  }
	| {
			kind: 'metadata_key'
			name: string
	  }
	| {
			kind: 'firmware'
			artifactId: string
			action?: FirmwareAccessAction
	  }
	| {
			kind: 'files'
			fileId: string
			action?: FileAccessAction
	  }
	| {
			kind: 'dbc_parser'
			parserId: string
			action?: DbcParserAccessAction
	  }
	| {
			kind: 'session_rule'
			ruleId: string
			action?: SessionRuleAccessAction
	  }
	| {
			kind: 'exporter'
			exporterId: string
			action?: ExporterAccessAction
	  }
	| {
			kind: 'subscriber_group'
			groupId: string
			action?: SubscriberGroupAccessAction
	  }
	| {
			kind: 'config'
			id: string
			action?: ConfigAccessAction
	  }
	| {
			kind: 'dashboard'
			dashboardId: number
			action?: 'view' | 'edit' | 'delete'
	  }

/**
 * Generic "is the user allowed by this AllOrSpecific value?" helper.
 */
function checkAllOrSpecific<T>(value: AllOrSpecific<T> | undefined, predicate: (specific: T) => boolean): boolean {
	if (!value) {
		return false
	}
	if (value === 'all') {
		return true
	}
	return predicate(value.some)
}

function filteredListAllows(list: FilteredList<string> | undefined, id: string): boolean {
	return checkAllOrSpecific(list, ids => {
		if (!Array.isArray(ids)) {
			return false
		}
		return ids.includes(id)
	})
}

function matchesKeyValueColumnFilter(
	filter: AllOrSpecific<Record<string, string[]>> | undefined,
	fields: Record<string, string>,
): boolean {
	return checkAllOrSpecific(filter, allowed => {
		const columns = Object.keys(allowed)
		if (columns.length === 0) {
			return true
		}
		return columns.every(col => {
			const allowedValues = allowed[col]
			const fieldValue = fields[col]
			if (fieldValue == null) {
				return false
			}
			// Role UI stores unrestricted column access as `['all']` (see device / metadata matrix editors).
			if (allowedValues.includes('all')) {
				return true
			}
			return allowedValues.includes(fieldValue)
		})
	})
}

/**
 * Match a device against `device:read`.
 */
export function canAccessDevice(permissions: Permissions | undefined, fields: Record<string, string>): boolean {
	return matchesKeyValueColumnFilter(permissions?.[PERMISSIONS.DEVICE_READ], fields)
}

/**
 * Match device metadata keys against `device-metadata:read` — a plain key set
 * (`AllOrSpecific<HashSet<String>>` on the backend), so every field name must be an
 * allowed key. Values in `fields` aren't filtered; access is per key, not per key/value.
 */
export function canAccessMetadataPairs(permissions: Permissions | undefined, fields: Record<string, string>): boolean {
	const filter = permissions?.[PERMISSIONS.DEVICE_METADATA_READ]
	return Object.keys(fields).every(key => filteredListAllows(filter, key))
}

/**
 * Match a metadata catalog key against `metadata:read` (`FilteredList<string>`).
 */
export function canAccessMetadataKey(permissions: Permissions | undefined, keyName: string): boolean {
	return filteredListAllows(permissions?.[PERMISSIONS.METADATA_READ], keyName)
}

export function canUpdateMetadataKey(permissions: Permissions | undefined, keyName: string): boolean {
	return filteredListAllows(permissions?.[PERMISSIONS.DEVICE_METADATA_UPDATE], keyName)
}

/**
 * Match a device metadata key against both grants that gate its visibility (see the
 * `DEVICE_METADATA_READ` comment in `permissions.ts`): `metadata:read` for the key definition
 * itself, and `device-metadata:read` for per-device values of that key.
 */
export function canAccessDeviceMetadataKey(permissions: Permissions | undefined, keyName: string): boolean {
	return canAccessMetadataKey(permissions, keyName) && canAccessMetadataPairs(permissions, { [keyName]: '' })
}

function streamInnerAllows(
	streamFilter: StreamAccessFilter | undefined,
	streamName: string,
	field: string | undefined,
): boolean {
	return checkAllOrSpecific(streamFilter, byStream => {
		const fieldsAllowed = byStream[streamName]
		if (!fieldsAllowed) {
			return false
		}
		if (!field) {
			return true
		}
		return checkAllOrSpecific(fieldsAllowed, allowedFields => allowedFields.includes(field))
	})
}

/**
 * Match a stream (and optionally a field) against `stream:read` plus the
 * action-specific stream-field permission:
 *
 *   - `view`   → `STREAM_READ` encodes both stream and field-level grants
 *   - `update` → `STREAM_FIELD_UPDATE` (always consulted when action is supplied)
 *   - `delete` → `STREAM_FIELD_DELETE` (always consulted when action is supplied)
 *
 * `STREAM_READ` is always the gate — a user that cannot read a stream cannot
 * mutate it either, regardless of the field-level grant.
 */
export function canAccessStream(
	permissions: Permissions | undefined,
	streamName: string,
	field?: string,
	action: StreamAccessAction = 'view',
): boolean {
	if (action === 'view' && streamName === ANOMALY_STREAM) {
		return true
	}
	const readOk = streamInnerAllows(permissions?.[PERMISSIONS.STREAM_READ], streamName, field)
	if (!readOk) {
		return false
	}

	if (action === 'view') {
		return true
	}

	if (action === 'delete') {
		if (field) {
			return streamInnerAllows(permissions?.[PERMISSIONS.STREAM_FIELD_DELETE], streamName, field)
		}

		return streamInnerAllows(permissions?.[PERMISSIONS.STREAM_DELETE], streamName, undefined)
	}

	if (action === 'update') {
		if (field) {
			return streamInnerAllows(permissions?.[PERMISSIONS.STREAM_FIELD_UPDATE], streamName, field)
		}

		return streamInnerAllows(permissions?.[PERMISSIONS.STREAM_UPDATE], streamName, undefined)
	}

	return false
}

/** Readable stream names, minus `device_state`. Pass a list already scoped to one tenant. */
export function accessibleStreamNames(permissions: Permissions | undefined, streams: { name: string }[]): string[] {
	return streams
		.map(stream => stream.name)
		.filter(name => name !== DEVICE_STATE_STREAM && canAccessStream(permissions, name))
}

/**
 * Match a device-state field against the action-specific `device-state-field:*` grant.
 * device_state is a single stream, so grants are a flat field-name list; update/delete
 * default to an empty list and grant nothing.
 */
export function canAccessDeviceStateField(
	permissions: Permissions | undefined,
	field?: string,
	action: DeviceStateFieldAccessAction = 'view',
): boolean {
	const key =
		action === 'update'
			? PERMISSIONS.DEVICE_STATE_FIELD_UPDATE
			: action === 'delete'
				? PERMISSIONS.DEVICE_STATE_FIELD_DELETE
				: PERMISSIONS.DEVICE_STATE_FIELD_READ
	const grant = permissions?.[key]
	if (field === undefined) {
		return grant === 'all'
	}
	return filteredListAllows(grant, field)
}

/**
 * Match an action type against filtered action-type grants.
 * `can_view` allows read, update, or execute lists. The write modes mirror the backend's
 * `has_action_type_write` (write list ∧ read list): `can_modify` requires `action-type:update`
 * and `can_delete` requires `action-type:delete`, each additionally requiring `action-type:read`.
 */
export function canAccessActionType(
	permissions: Permissions | undefined,
	actionTypeName: string,
	requiredMode: ActionTypeAccessMode,
): boolean {
	const read = filteredListAllows(permissions?.[PERMISSIONS.ACTION_TYPE_READ], actionTypeName)
	if (requiredMode === 'can_view') {
		const update = filteredListAllows(permissions?.[PERMISSIONS.ACTION_TYPE_UPDATE], actionTypeName)
		const execute = filteredListAllows(permissions?.[PERMISSIONS.ACTION_TYPE_EXECUTE], actionTypeName)
		return read || update || execute
	}
	if (requiredMode === 'can_delete') {
		return read && filteredListAllows(permissions?.[PERMISSIONS.ACTION_TYPE_DELETE], actionTypeName)
	}
	return read && filteredListAllows(permissions?.[PERMISSIONS.ACTION_TYPE_UPDATE], actionTypeName)
}

/** True when the role may execute at least one action type in `actionTypeNames`. */
export function hasAnyExecutableActionType(
	permissions: Permissions | undefined,
	actionTypeNames: readonly string[],
): boolean {
	const execute = permissions?.[PERMISSIONS.ACTION_TYPE_EXECUTE]
	if (!execute) {
		return false
	}
	if (execute === 'all') {
		return actionTypeNames.length > 0
	}
	return actionTypeNames.some(name => filteredListAllows(execute, name))
}

export function canExecuteActionType(permissions: Permissions | undefined, actionTypeName: string): boolean {
	return filteredListAllows(permissions?.[PERMISSIONS.ACTION_TYPE_EXECUTE], actionTypeName)
}

/**
 * Same as `canAccessActionType` but for action type **ids** when grants store ids instead of names.
 */
export function canAccessActionTypeId(
	permissions: Permissions | undefined,
	actionTypeId: string,
	requiredMode: ActionTypeAccessMode,
): boolean {
	return canAccessActionType(permissions, actionTypeId, requiredMode)
}

export function canAccessFirmware(
	permissions: Permissions | undefined,
	artifactId: string,
	action: FirmwareAccessAction = 'view',
): boolean {
	switch (action) {
		case 'view':
			return filteredListAllows(permissions?.[PERMISSIONS.FIRMWARE_READ], artifactId)
		case 'download':
			return filteredListAllows(permissions?.[PERMISSIONS.FIRMWARE_DOWNLOAD], artifactId)
		case 'edit':
			return filteredListAllows(permissions?.[PERMISSIONS.FIRMWARE_UPDATE], artifactId)
		case 'delete':
			return filteredListAllows(permissions?.[PERMISSIONS.FIRMWARE_DELETE], artifactId)
		default: {
			const _exhaustive: never = action
			return _exhaustive
		}
	}
}

export function canAccessFile(
	permissions: Permissions | undefined,
	fileId: string,
	action: FileAccessAction = 'view',
): boolean {
	switch (action) {
		case 'view':
			return filteredListAllows(permissions?.[PERMISSIONS.FILES_READ], fileId)
		case 'download':
			return filteredListAllows(permissions?.[PERMISSIONS.FILES_DOWNLOAD], fileId)
		case 'edit':
			return filteredListAllows(permissions?.[PERMISSIONS.FILES_UPDATE], fileId)
		case 'delete':
			return filteredListAllows(permissions?.[PERMISSIONS.FILES_DELETE], fileId)
	}
}

export function canAccessDbcParser(
	permissions: Permissions | undefined,
	parserId: string,
	action: DbcParserAccessAction = 'view',
): boolean {
	switch (action) {
		case 'view':
			return filteredListAllows(permissions?.[PERMISSIONS.DBC_PARSER_READ], parserId)
		case 'edit':
			return filteredListAllows(permissions?.[PERMISSIONS.DBC_PARSER_UPDATE], parserId)
		case 'delete':
			return filteredListAllows(permissions?.[PERMISSIONS.DBC_PARSER_DELETE], parserId)
		case 'download':
			return filteredListAllows(permissions?.[PERMISSIONS.DBC_PARSER_FILE_DOWNLOAD], parserId)
		case 'reconfigure':
			return filteredListAllows(permissions?.[PERMISSIONS.DBC_PARSER_RECONFIGURE], parserId)
	}
}

/**
 * Match a configuration asset against the action-specific `config:*` grant. JSON and geofence
 * configs share this filtered list, keyed by the asset id (`config:*.some[]`) — unlike
 * files/firmware grants, which key by version. Defaults to `'view'` (`CONFIG_READ`).
 */
export function canAccessConfig(
	permissions: Permissions | undefined,
	configId: string,
	action: ConfigAccessAction = 'view',
): boolean {
	switch (action) {
		case 'view':
			return filteredListAllows(permissions?.[PERMISSIONS.CONFIG_READ], configId)
		case 'edit':
			return filteredListAllows(permissions?.[PERMISSIONS.CONFIG_UPDATE], configId)
		case 'delete':
			return filteredListAllows(permissions?.[PERMISSIONS.CONFIG_DELETE], configId)
	}
}

export function canAccessSessionRule(
	permissions: Permissions | undefined,
	ruleId: string,
	action: SessionRuleAccessAction = 'view',
): boolean {
	const key =
		action === 'update'
			? PERMISSIONS.SESSION_RULE_UPDATE
			: action === 'delete'
				? PERMISSIONS.SESSION_RULE_DELETE
				: PERMISSIONS.SESSION_RULE_READ
	return filteredListAllows(permissions?.[key], ruleId)
}

/**
 * Match a subscriber group id against the action-specific filtered-list grant.
 * Defaults to `'view'` (i.e. `SUBSCRIBER_GROUP_READ`).
 */
export function canAccessSubscriberGroup(
	permissions: Permissions | undefined,
	groupId: string,
	action: SubscriberGroupAccessAction = 'view',
): boolean {
	const key =
		action === 'update'
			? PERMISSIONS.SUBSCRIBER_GROUP_UPDATE
			: action === 'delete'
				? PERMISSIONS.SUBSCRIBER_GROUP_DELETE
				: PERMISSIONS.SUBSCRIBER_GROUP_READ
	return filteredListAllows(permissions?.[key], groupId)
}

/**
 * Match an exporter id against the action-specific filtered-list grant.
 * Defaults to `'view'` (i.e. `EXPORTER_READ`).
 */
export function canAccessExporter(
	permissions: Permissions | undefined,
	exporterId: string,
	action: ExporterAccessAction = 'view',
): boolean {
	const key =
		action === 'update'
			? PERMISSIONS.EXPORTER_UPDATE
			: action === 'delete'
				? PERMISSIONS.EXPORTER_DELETE
				: PERMISSIONS.EXPORTER_READ
	return filteredListAllows(permissions?.[key], exporterId)
}

export function canAccessDashboard(
	permissions: Permissions | undefined,
	dashboardId: number,
	action: 'view' | 'edit' | 'delete' = 'view',
): boolean {
	const key =
		action === 'edit'
			? PERMISSIONS.DASHBOARD_UPDATE
			: action === 'delete'
				? PERMISSIONS.DASHBOARD_DELETE
				: PERMISSIONS.DASHBOARD_READ
	const grant = permissions?.[key]
	if (!grant) {
		return false
	}
	if (grant === 'all') {
		return true
	}
	if (typeof grant === 'object' && 'some' in grant && Array.isArray(grant.some)) {
		return (
			grant.some.includes(dashboardId) ||
			grant.some.includes(Number(dashboardId)) ||
			grant.some.includes(String(dashboardId) as never)
		)
	}
	return false
}

/**
 * Single dispatcher that takes any EntityDescriptor and returns the access result.
 */
export function checkEntityAccess(permissions: Permissions | undefined, entity: EntityDescriptor): boolean {
	switch (entity.kind) {
		case 'device':
			return canAccessDevice(permissions, entity.fields)
		case 'stream':
			return canAccessStream(permissions, entity.name, entity.field, entity.action)
		case 'device_state_field':
			return canAccessDeviceStateField(permissions, entity.field, entity.action ?? 'view')
		case 'action_type':
			return canAccessActionType(permissions, entity.name, entity.mode)
		case 'action_type_id':
			return canAccessActionTypeId(permissions, entity.id, entity.mode)
		case 'metadata':
			return canAccessMetadataPairs(permissions, entity.fields)
		case 'metadata_key':
			return canAccessMetadataKey(permissions, entity.name)
		case 'firmware':
			return canAccessFirmware(permissions, entity.artifactId, entity.action ?? 'view')
		case 'files':
			return canAccessFile(permissions, entity.fileId, entity.action ?? 'view')
		case 'dbc_parser':
			return canAccessDbcParser(permissions, entity.parserId, entity.action)
		case 'session_rule':
			return canAccessSessionRule(permissions, entity.ruleId, entity.action)
		case 'exporter':
			return canAccessExporter(permissions, entity.exporterId, entity.action)
		case 'subscriber_group':
			return canAccessSubscriberGroup(permissions, entity.groupId, entity.action)
		case 'config':
			return canAccessConfig(permissions, entity.id, entity.action ?? 'view')
		case 'dashboard':
			return canAccessDashboard(permissions, entity.dashboardId, entity.action ?? 'view')
	}
}

export interface EntitySetVerdict {
	allAllowed: boolean
	allowedCount: number
	deniedCount: number
	total: number
	firstDenied?: EntityDescriptor
}

/**
 * Evaluate a set of entity descriptors against a single permissions object.
 */
export function checkEntitySetAccess(
	permissions: Permissions | undefined,
	entities: EntityDescriptor[],
): EntitySetVerdict {
	let allowedCount = 0
	let deniedCount = 0
	let firstDenied: EntityDescriptor | undefined
	for (const entity of entities) {
		if (checkEntityAccess(permissions, entity)) {
			allowedCount++
		} else {
			deniedCount++
			if (!firstDenied) {
				firstDenied = entity
			}
		}
	}
	return {
		allAllowed: deniedCount === 0,
		allowedCount,
		deniedCount,
		total: entities.length,
		firstDenied,
	}
}
